import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Table,
  Container,
  Row,
  Col,
  Form,
  Spinner,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import API from "../utils/api";
import swal from "sweetalert";
import * as XLSX from "xlsx";

const getTotalReal = (p) =>
  Number(p.importeOriginal || 0) + Number(p.montoInteres || 0);

const SupervisorPagos = () => {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroCobrador, setFiltroCobrador] = useState("");
  const [filtroProcesado, setFiltroProcesado] = useState("pendientes");
  const [pagosSeleccionados, setPagosSeleccionados] = useState([]);
  const [sincronizando, setSincronizando] = useState(false);
  const [progreso, setProgreso] = useState({
    total: 0,
    actual: 0,
  });
  const [intereses, setIntereses] = useState({});
  const [guardandoInteres, setGuardandoInteres] = useState({});
  const [estadoInteres, setEstadoInteres] = useState({});

  // ================================
  // CARGAR PAGOS
  // ================================
  const quitarPagoDelListado = (idPago) => {
    setPagos((prev) => prev.filter((p) => p.idPago !== idPago));
    setPagosSeleccionados((prev) => prev.filter((p) => p.idPago !== idPago));
  };

  const exportarExcelSupervisor = () => {
    if (pagosFiltrados.length === 0) {
      swal("Sin datos", "No hay pagos para exportar", "warning");
      return;
    }

    const data = pagosFiltrados.map((p) => {
      const estado = obtenerEstadoPago(p);
      const interes = Number(p.montoInteres || 0);

      return {
        Fecha: p.fechaPago
          ? new Date(p.fechaPago).toLocaleDateString("es-AR", {
              timeZone: "UTC",
            })
          : "",
        Tipo: p.tipo,
        Número: p.numero,
        Cuota: p.cuota,
        Cliente: p.cliente,

        "Importe Original": Number(p.importeOriginal),
        Interés: interes,

        // 👉 opcional pero MUY recomendado
        "Total a Cobrar": Number(p.importeOriginal) + interes,

        "Monto Pagado": Number(p.montoPagado),

        Medio: p.medioPago || "",
        Cobrador: p.cobrador,
        Estado: estado.label,
        Procesado: p.procesado === 1 ? "Sí" : "No",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(data);

    // 🔹 Auto ancho de columnas (simple y efectivo)
    const cols = Object.keys(data[0]).map((key) => ({
      wch: Math.max(
        key.length,
        ...data.map((row) => String(row[key] ?? "").length)
      ),
    }));
    worksheet["!cols"] = cols;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Pagos");

    const nombreArchivo = `Pagos_Supervisor_${new Date()
      .toISOString()
      .slice(0, 10)}.xlsx`;

    XLSX.writeFile(workbook, nombreArchivo);
  };

  const cargarPagos = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API.urlAPI}pagoscobradores`);
      setPagos(res.data);
    } catch {
      swal("Error", "No se pudieron cargar los pagos", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarPagos();
  }, []);
  useEffect(() => {
    const init = {};
    pagos.forEach((p) => {
      init[p.idPago] = Number(p.montoInteres || 0);
    });
    setIntereses(init);
  }, [pagos]);

  // ================================
  // ESTADO VISUAL
  // ================================
  const obtenerEstadoPago = (p) => {
    const totalReal = getTotalReal(p);
    const pagado = Number(p.montoPagado);

    if (p.procesado === 1) {
      return { label: "Procesado", variant: "success" };
    }

    if (pagado > 0 && pagado < totalReal) {
      return { label: "Parcial", variant: "warning", text: "dark" };
    }

    return { label: "Pendiente", variant: "danger" };
  };

  // ================================
  // FILTROS
  // ================================
  const pagosFiltrados = pagos.filter((p) => {
    const textoFiltro = filtroCobrador.trim().toLowerCase();

    const coincideClienteONumero =
      textoFiltro === "" ||
      (p.cliente || "").toLowerCase().includes(textoFiltro) ||
      String(p.numero || "").includes(textoFiltro);

    const esPendiente = Number(p.montoPagado) === 0 && p.procesado === 0;
    const totalReal = getTotalReal(p);

    const esParcial =
      Number(p.montoPagado) > 0 &&
      Number(p.montoPagado) < totalReal &&
      p.procesado === 0;

    const esProcesado = p.procesado === 1;

    let coincideEstado = true;

    switch (filtroProcesado) {
      case "pendientes":
        coincideEstado = esPendiente;
        break;
      case "parciales":
        coincideEstado = esParcial;
        break;
      case "pendientes_parciales":
        coincideEstado = esPendiente || esParcial;
        break;
      case "procesados":
        coincideEstado = esProcesado;
        break;
      default:
        coincideEstado = true;
    }

    return coincideClienteONumero && coincideEstado;
  });

  // ================================
  // TOTALES
  // ================================
  const totalPendientes = pagos.filter(
    (p) => p.procesado === 0 && Number(p.montoPagado) === 0
  ).length;

  const totalParciales = pagos.filter((p) => {
    const totalReal = getTotalReal(p);
    const pagado = Number(p.montoPagado);
    return p.procesado === 0 && pagado > 0 && pagado < totalReal;
  }).length;

  const totalProcesados = pagos.filter((p) => p.procesado === 1).length;

  // ================================
  // SELECCIÓNa
  // ================================
  const esSeleccionable = (p) => {
    const totalReal = getTotalReal(p);
    const pagado = Number(p.montoPagado);
    return p.procesado === 1 || (pagado > 0 && pagado < totalReal);
  };

  const toggleSeleccion = (pago) => {
    setPagosSeleccionados((prev) => {
      const existe = prev.find((p) => p.idPago === pago.idPago);

      return existe
        ? prev.filter((p) => p.idPago !== pago.idPago)
        : [...prev, pago];
    });
  };
  const seleccionarTodosVisibles = (checked) => {
    if (!checked) {
      setPagosSeleccionados([]);
      return;
    }

    const seleccionables = pagosFiltrados.filter(esSeleccionable);
    setPagosSeleccionados(seleccionables);
  };

  const todosSeleccionados =
    pagosFiltrados.filter(esSeleccionable).length > 0 &&
    pagosFiltrados
      .filter(esSeleccionable)
      .every((p) => pagosSeleccionados.some((s) => s.idPago === p.idPago));

  // ================================
  // ELIMINAR
  // ================================
  const eliminarPago = async (pago) => {
    if (pago.procesado === 1) {
      swal("Acción no permitida", "Pago ya procesado", "warning");
      return;
    }

    const confirm = await swal({
      title: "¿Eliminar pago?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirm) return;

    try {
      await axios.delete(`${API.urlAPI}pagoscobradores/${pago.idPago}`);
      swal("Eliminado", "Pago eliminado", "success");
      cargarPagos();
    } catch {
      swal("Error", "No se pudo eliminar", "error");
    }
  };

  const guardarInteres = async (pago) => {

     if (pago.procesado === 1) return;
    const montoInteres = Number(intereses[pago.idPago] || 0);

    try {
      setGuardandoInteres((prev) => ({ ...prev, [pago.idPago]: true }));

      await axios.put(`${API.urlAPI}pagoscobradores/interes/${pago.idPago}`, {
        montoInteres,
      });

      setPagos((prev) =>
        prev.map((p) => (p.idPago === pago.idPago ? { ...p, montoInteres } : p))
      );

      setEstadoInteres((prev) => ({
        ...prev,
        [pago.idPago]: "ok",
      }));

      // limpiar feedback después de 1.5s
      setTimeout(() => {
        setEstadoInteres((prev) => {
          const copy = { ...prev };
          delete copy[pago.idPago];
          return copy;
        });
      }, 1500);
    } catch {
      setEstadoInteres((prev) => ({
        ...prev,
        [pago.idPago]: "error",
      }));
    } finally {
      setGuardandoInteres((prev) => ({ ...prev, [pago.idPago]: false }));
    }
  };

  // ================================
  // SINCRONIZAR
  // ================================
  const sincronizarPagos = async () => {
    if (pagosSeleccionados.length === 0) return;

    const confirm = await swal({
      title: "¿Sincronizar pagos?",
      text: `Se impactarán ${pagosSeleccionados.length} cuotas`,
      icon: "warning",
      buttons: true,
    });

    if (!confirm) return;

    // ✅ SNAPSHOT (CLAVE)
    const pagosAProcesar = [...pagosSeleccionados];

    try {
      setLoading(true);
      setSincronizando(true);
      setProgreso({
        total: pagosAProcesar.length,
        actual: 0,
      });

      for (const p of pagosAProcesar) {
  try {
    const capital = Number(p.importeOriginal || 0);
    const interes = Number(p.montoInteres || 0);
    const totalReal = capital + interes;

    const montoPagado = Math.round(Number(p.montoPagado || 0));

    const esPagoCompleto = montoPagado >= totalReal;

    const estado = esPagoCompleto
      ? "Pago Completo"
      : "Pago Parcial";

    const medioPago =
      p.medioPago === "Transferencia" || p.medioPago === "TR"
        ? "TR"
        : "EF";

    let payload;

    // 🔹 CRÉDITOS
    if (p.tipo === "Credito") {
      payload = {
        Estado: estado,
        MontoPago: montoPagado,
        medioPago,
        CuotaConInteres: totalReal,
      };

      await axios.put(
        `${API.urlAPI}prestamodetalle/PagoCuota/${p.idCuota}`,
        payload
      );
    }

    // 🔹 VENTAS
    else if (p.tipo === "Venta") {
      payload = {
        MontoPago: String(montoPagado),
        Estado: estado,
        FechaRegistro: new Date().toISOString(),
        medioPago,
        CuotaConInteres: totalReal,
      };

      await axios.put(
        `${API.urlAPI}ventacuotasdetalles/PagoCuota/${p.idCuota}`,
        payload
      );
    } else {
      throw new Error("Tipo de pago desconocido");
    }

    await axios.delete(`${API.urlAPI}pagoscobradores/${p.idPago}`);

    setProgreso((prev) => ({
      ...prev,
      actual: prev.actual + 1,
    }));
  } catch (error) {
    console.error("❌ Error procesando pago", p, error);
    swal(
      "Error",
      `No se pudo sincronizar el pago ID ${p.idPago}`,
      "error"
    );
  }
}

      /*
      for (const p of pagosAProcesar) {
  try {
    const montoPago = Math.round(Number(p.montoPagado));

    const medioPago =
      p.medioPago === "Transferencia" || p.medioPago === "TR"
        ? "TR"
        : "EF";

    const estado = "Pago Completo"; // 🔒 ya viene validado

    let payload;

    // 🔹 CRÉDITOS
    if (p.tipo === "Credito") {
      payload = {
        Estado: estado,
        MontoPago: montoPago,
        medioPago,
        CuotaConInteres: montoPago, // ✅ MISMO VALOR
      };

      await axios.put(
        `${API.urlAPI}prestamodetalle/PagoCuota/${p.idCuota}`,
        payload
      );
    }

    // 🔹 VENTAS
    else if (p.tipo === "Venta") {
      payload = {
        MontoPago: String(montoPago),
        Estado: estado,
        FechaRegistro: new Date().toISOString(),
        medioPago,
        CuotaConInteres: montoPago, // ✅ MISMO VALOR
      };

      await axios.put(
        `${API.urlAPI}ventacuotasdetalles/PagoCuota/${p.idCuota}`,
        payload
      );
    } else {
      throw new Error("Tipo de pago desconocido");
    }

    // 🔥 eliminar de pagos cobradores
    await axios.delete(`${API.urlAPI}pagoscobradores/${p.idPago}`);

    setProgreso((prev) => ({
      ...prev,
      actual: prev.actual + 1,
    }));
  } catch (error) {
    console.error("❌ Error procesando pago", p, error);
    swal(
      "Error",
      `No se pudo sincronizar el pago ID ${p.idPago}`,
      "error"
    );
  }
}

*/

      // ✅ LIMPIEZA FINAL (UNA SOLA VEZ)
      setPagos((prev) =>
        prev.filter((p) => !pagosAProcesar.some((x) => x.idPago === p.idPago))
      );

      setPagosSeleccionados([]);

      swal("Éxito", "Pagos sincronizados", "success");
    } catch {
      swal("Error", "Error al sincronizar", "error");
    } finally {
      setLoading(false);
      setSincronizando(false);
    }
  };

  return (
    <Container>
      {sincronizando && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0,0,0,0.45)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card style={{ width: "380px", textAlign: "center" }}>
            <Card.Body>
              <Spinner
                animation="border"
                variant="primary"
                style={{ width: 50, height: 50 }}
              />

              <h5 className="mt-3">Sincronizando pagos…</h5>

              <p className="text-muted mb-2">
                {progreso.actual} de {progreso.total} procesados
              </p>

              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={{
                    width: `${(progreso.actual / progreso.total) * 100 || 0}%`,
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </div>
      )}

      <br />
      <Card>
        <Card.Header style={{ background: "#005187", color: "white" }}>
          <h3>Control de Pagos – Supervisor</h3>
        </Card.Header>

        <Card.Body>
          <Row className="mb-3 text-center">
            <Col>
              <Badge bg="danger">Pendientes: {totalPendientes}</Badge>
            </Col>
            <Col>
              <Badge bg="warning" text="dark">
                Parciales: {totalParciales}
              </Badge>
            </Col>
            <Col>
              <Badge bg="success">Procesados: {totalProcesados}</Badge>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col className="text-end">
              <strong>Seleccionados: {pagosSeleccionados.length}</strong>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={4}>
              <Form.Control
                placeholder="Filtrar por Cliente o Número..."
                value={filtroCobrador}
                onChange={(e) => setFiltroCobrador(e.target.value)}
              />
            </Col>

            <Col md={4}>
              <Form.Select
                value={filtroProcesado}
                onChange={(e) => setFiltroProcesado(e.target.value)}
              >
                <option value="pendientes">Pendientes</option>
                <option value="parciales">Parciales</option>
                <option value="pendientes_parciales">
                  Pendientes y Parciales
                </option>
                <option value="procesados">Procesados</option>
                <option value="todos">Todos</option>
              </Form.Select>
            </Col>

            <Col
              md={4}
              className="text-center d-flex justify-content-center gap-2"
            >
              <Button
                variant="success"
                disabled={pagosSeleccionados.length === 0 || loading}
                onClick={sincronizarPagos}
              >
                Sincronizar Pagos
              </Button>

              <Button
                variant="outline-success"
                onClick={exportarExcelSupervisor}
                disabled={pagosFiltrados.length === 0}
              >
                Exportar Excel
              </Button>
            </Col>
          </Row>

          <Table striped bordered hover responsive>
            <thead>
              <tr style={{ background: "#5589c4", color: "white" }}>
                <th>
                  <Form.Check
                    checked={todosSeleccionados}
                    onChange={(e) => seleccionarTodosVisibles(e.target.checked)}
                  />
                </th>
                <th>Fecha Pago</th>
                <th>Vencimiento</th>
                <th>Tipo</th>
                <th>Número</th>
                <th>Cuota</th>
                <th>Cliente</th>
                <th>Importe</th>
                <th>Interés</th>
                <th>Pagado</th>
                <th>Medio</th>
                <th>Observaciones</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>

            <tbody>
              {pagosFiltrados.map((p) => {
                const estado = obtenerEstadoPago(p);

                return (
                  <tr key={p.idPago}>
                    <td className="text-center">
                      <Form.Check
                        disabled={!esSeleccionable(p)}
                        checked={pagosSeleccionados.some(
                          (x) => x.idPago === p.idPago
                        )}
                        onChange={() => toggleSeleccion(p)}
                      />
                    </td>

                    <td>
                      {p.fechaPago
                        ? new Date(p.fechaPago).toLocaleDateString("es-AR")
                        : "-"}
                    </td><td>
  {p.fechaRegistro
    ? new Date(p.fechaRegistro).toLocaleDateString("es-AR")
    : "-"}
</td>

                    <td>{p.tipo}</td>
                    <td>{p.numero}</td>
                    <td>{p.cuota}</td>
                    <td>{p.cliente}</td>

                    {/* IMPORTE ORIGINAL (SIN TOCAR) */}
                    <td>${Number(p.importeOriginal).toLocaleString()}</td>

                    {/* 👉 INTERÉS (NUEVO) */}
                    <td style={{ maxWidth: 130 }}>
                      <div className="d-flex align-items-center gap-1">
                        <Form.Control
                          type="number"
                          min={0}
                          step={1}
                          size="sm"
                          value={intereses[p.idPago] ?? 0}
                          disabled={
                            guardandoInteres[p.idPago] || p.procesado === 1
                          }
                          onChange={(e) =>
                            setIntereses((prev) => ({
                              ...prev,
                              [p.idPago]: Math.max(
                                0,
                                parseInt(e.target.value || 0)
                              ),
                            }))
                          }
                          onBlur={() => guardarInteres(p)}
                        />

                        {estadoInteres[p.idPago] === "ok" && (
                          <span style={{ color: "green" }}>✔</span>
                        )}

                        {estadoInteres[p.idPago] === "error" && (
                          <span style={{ color: "red" }}>✖</span>
                        )}
                      </div>
                    </td>

                    {/* PAGADO (SIN TOCAR) */}
                    <td>${Number(p.montoPagado).toLocaleString()}</td>

                    <td>{p.medioPago || "-"}</td>
                    <td>{p.observaciones}</td>

                    <td>
                      <Badge bg={estado.variant} text={estado.text || "white"}>
                        {estado.label}
                      </Badge>
                    </td>

                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={p.procesado === 1}
                        onClick={() => eliminarPago(p)}
                      >
                        🗑️
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SupervisorPagos;
